import { gql, useMutation } from '@apollo/client'

import {
  AddReplyDocumentCommentMutation,
  AddReplyDocumentCommentMutationVariables,
} from '../components/__generated__/mutations.graphql'
import { AddReply } from '../components/mutations'
import { UseAddReply_DocumentFragment } from './__generated__/use-add-reply.graphql'

export type UseAddReplyProps = {
  document: UseAddReply_DocumentFragment
  commentText: string
  commentId: number
}

export type UseAddReplyReturn = {
  loading: boolean
  addReply: () => Promise<void>
}

export function useAddReply({
  document,
  commentText,
  commentId,
}: UseAddReplyProps): UseAddReplyReturn {
  const { docId, artifact } = document
  const [mutate, { loading }] = useMutation<
    AddReplyDocumentCommentMutation,
    AddReplyDocumentCommentMutationVariables
  >(AddReply, {
    update(cache, { data: mutate }) {
      cache.modify<{ numComments: number }>({
        id: cache.identify(document),
        fields: {
          numComments() {
            return mutate?.addReplyDocumentComment?.document?.numComments || 0
          },
        },
      })

      const commentParentId = mutate?.addReplyDocumentComment?.commentParent?.docId
      cache.modify({
        id: `DocumentComment:${commentParentId}`,
        fields: {
          replies(repliesFromCache = []) {
            const reply = mutate?.addReplyDocumentComment?.reply
            const replyNodeRef = cache.writeFragment({
              data: {
                ...reply,
                __typename: 'DocumentComment',
                id: reply?.docId,
                profile: reply?.publisher,
                viewerUpVoted: reply?.viewerLiked,
                viewerDownVoted: !reply?.viewerLiked,
                upVotes: reply?.votesAmount,
                parentCommentId: commentParentId,
              },
              fragment: useAddReply.fragments.reply,
            })
            return [...repliesFromCache, replyNodeRef]
          },
        },
      })
    },
  })

  return {
    async addReply() {
      const input = { docId, artifact, text: commentText, commentId }
      await mutate({ variables: { input } })
    },
    loading,
  }
}

useAddReply.fragments = {
  document: gql`
    fragment UseAddReply_document on Document {
      docId
      artifact
    }
  `,
  reply: gql`
    fragment UseAddReply_comment on DocumentComment {
      id
      text
      date
      profile {
        url
        fullname
        thumborImage {
          current: thumbUrl(size: "50x50")
        }
        hasProBadge
        isViewer
      }
      parentCommentId
      viewerUpVoted
      upVotes
      viewerDownVoted
    }
  `,
}
